<template>
  <AppWithLoading :fetch="fetchCompany" v-slot:default="{loader}">
    <div>
      <VCardText>
        <div class="mb-10 tab" v-if="!isImported">
          <template v-if="tickets.length === 0">
            <VAlert
              v-if="isWrongFile"
              border="right"
              colored-border
              type="error"
              elevation="2"
            >
              {{ $t('wrongFile.1') }} <a :href="templateLink">{{ $t('useTemplate') }}</a>. {{ $t('wrongFile.2') }} <a href="#">{{ $t('useTechnicalSupport') }}</a>
            </VAlert>
            <button
              class="tab__button"
              :loading="isSelecting"
              @click="onButtonClick"
            >
              <VIcon>
                mdi-upload
              </VIcon>
              <span
                class="tab__button-text"
              >{{ buttonText }}</span>
            </button>
            <input
              ref="uploader"
              class="d-none"
              type="file"
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              @change="onFileChanged"
            >
            <a class="tab__button-download" :href="templateLink">
              {{ $t('downloadTemplate') }}
            </a>
          </template>
          <div v-else>
            <div class="mb-5">
              {{ $tc('importedCandidatesCount', tickets.length) }}
            </div>
            <VDataTable
              dense
              outlined
              :headers="tableData.headers[locale]"
              :items="tickets"
              :items-per-page="5"
              class="elevation-1 align-center"
            >
              <template v-slot:item.email="{ item }">
                <span v-if="invalidEmail" :style="!emailRegex.test(item.email) ? 'color: red' : 'color: black'">
                  {{ item.email }}
                </span>
                <span v-else-if="duplicatedEmails" :style="checkIfDuplicatedEmail(item.email) ? 'color: red' : 'color: black'">
                  {{ item.email }}
                </span>
                <span v-else>
                  {{ item.email }}
                </span>
              </template>
              <template v-slot:item.actions="{ item }">
                <VIcon
                  small
                  @click="deleteItem(item)"
                >
                  mdi-delete
                </VIcon>
              </template>
            </VDataTable>
            <VAlert
              class="mt-2"
              v-if="invalidEmail"
              border="right"
              colored-border
              type="error"
              elevation="2"
            >
              {{ $t('invalidEmail') }}
            </VAlert>
            <VAlert
              class="mt-2"
              v-else-if="duplicatedEmails && duplicatedEmails.length !== 0"
              border="right"
              colored-border
              type="error"
              elevation="2"
            >
              {{ $t('duplicatedEmails') }}
            </VAlert>
            <VAlert
              class="mt-2"
              v-else-if="invalidNumber"
              border="right"
              colored-border
              type="error"
              elevation="2"
            >
              {{ $t('invalidNumber') }}
            </VAlert>
            <QuotaResolver v-slot:default="{quota}" class="notify">
              <VRadioGroup v-model="notifyBy">
                <VRadio
                  class="notify__item"
                  v-for="notifyType in notifyTypes"
                  :key="notifyType.value"
                  :value="notifyType.value"
                >
                  <template #label>
                    {{ notifyType.text }}
                    <span v-if="notifyType.value !== 'Email' && notifyType" class="notify__rest-amount">
                      ({{ notifyType.quotaCode }})
                      <span
                        class="notify__rest-hint"
                        :class="{'warning-SMSes': ticketsMoreThanSMSes, 'warning-calls': ticketsMoreThanCalls}"
                      >
                        {{ $tc(`notifyBy${notifyType.value}Tooltip`, notifyType.quotaCode) + ". " }}
                        <router-link href="/settings/payment/plans" target="_blank">
                          Сменить тариф
                        </router-link>
                      </span>
                    </span>
                  </template>
                </VRadio>
              </VRadioGroup>
              <template v-if="notifyBy !== '' && phoneCount > quota[getQuotaCodeByNotifyBy(notifyBy)] && quota[getQuotaCodeByNotifyBy(notifyBy)] !== 0">
                <VAlert
                  border="bottom"
                  colored-border
                  type="warning"
                  elevation="2"
                >
                  {{ getQuotaAlertText(quota[getQuotaCodeByNotifyBy(notifyBy)]) }}
                </VAlert>
              </template>
            </QuotaResolver>
            <VRow>
              <VBtn
                :disabled="invalidEmail"
                class="btn mt-5 ml-3"
                id="inviteBtn"
                color="success"
                @click="loader(() => submit())"
              >
                {{ $t('invite') }}
              </VBtn>
              <VBtn
                class="btn mt-5 ml-5"
                color="success"
                outlined
                @click="cancel"
              >
                {{ $t('cancel') }}
              </VBtn>
            </VRow>
          </div>
        </div>
        <div class="mb-10" v-if="isImported">
          <VAlert
            dense
            text
            type="success"
          >
            {{ $tc('invitationsSent', importedCount) }}
          </VAlert>
        </div>
      </VCardText>
    </div>
  </AppWithLoading>
</template>

<script>
import XLSX from 'xlsx';
import { useI18n } from 'vue-i18n-composable';
import { candidateNotificationTypes, invitationType, quotaItemTypes } from '@/enums';
import QuotaResolver from '@/components/Payment/Quota/QuotaResolver';
import { computed, ref } from '@vue/composition-api';
import AppWithLoading from '@/components/AppWithLoading';
import useCandidates from '@/compositions/useCandidates';
import { format, parse } from 'date-fns';
import { DATE_FORMATS } from '@/date-utils';
import parsePhoneNumber from 'libphonenumber-js';
import useCompany from '@/compositions/useCompany';
import useAccount from '@/compositions/accounting/useAccount';

export default {
  name: 'ImportFromExcel',
  components: { AppWithLoading, QuotaResolver },
  props: {
    vacancy: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      defaultButtonText: '',
      selectedFile: null,
      isSelecting: false,
      isImported: false,
      isWrongFile: false,
      invalidNumber: false,
      repeatedEmails: {},
      importedCount: null,
      emailRegex: /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i,
      withInvitation: true,
      tableData: {
        headers: {
          ru: [
            {
              text: 'Имя',
              sortable: true,
              value: 'firstName',
              fromData: true,
            },
            {
              text: 'Фамилия',
              sortable: true,
              value: 'lastName',
              fromData: true,
            },
            {
              text: 'Email',
              sortable: true,
              value: 'email',
              fromData: true,
            },
            {
              text: 'Телефон',
              sortable: true,
              value: 'phone',
              fromData: true,
            },
            {
              text: 'Дата рождения',
              sortable: true,
              value: 'birthDate',
              fromData: true,
            },
            {
              text: 'Город',
              sortable: true,
              value: 'location',
              fromData: true,
            },
            {
              text: 'Действия',
              value: 'actions',
              sortable: false,
              fromData: false,
            },
          ],
          en: [
            {
              text: 'First name',
              sortable: true,
              value: 'firstName',
              fromData: true,
            },
            {
              text: 'Last name',
              sortable: true,
              value: 'lastName',
              fromData: true,
            },
            {
              text: 'Email',
              sortable: true,
              value: 'email',
              fromData: true,
            },
            {
              text: 'Phone',
              sortable: true,
              value: 'phone',
              fromData: true,
            },
            {
              text: 'Birth date',
              sortable: true,
              value: 'birthDate',
              fromData: true,
            },
            {
              text: 'City',
              sortable: true,
              value: 'location',
              fromData: true,
            },
            {
              text: 'Actions',
              value: 'actions',
              sortable: false,
              fromData: false,
            },
          ],
        },
        requiredFields: [
          'firstName',
          'lastName',
          'email',
        ],
      },
    };
  },
  computed: {
    buttonText() {
      return this.selectedFile ? this.selectedFile.name : this.defaultButtonText;
    },
    duplicatedEmails() {
      const emails = this.tickets.map((ticket) => ticket.email);
      return emails.filter((email, idx) => emails.indexOf(email) !== idx)
        .filter((value, idx, self) => self.indexOf(value) === idx);
    },
    phoneCount() {
      return this.tickets.filter((ticket) => !!ticket.phone && ticket.phone !== '').length;
    },
    invalidEmail() {
      return this.tickets.some((ticket) => !this.emailRegex.test(ticket.email.toLowerCase()));
    },
  },
  created() {
    this.defaultButtonText = this.$t('buttonText');
  },
  setup() {
    const { company, fetch } = useCompany();
    const { quota, fetchIfNull } = useAccount();

    async function fetchCompany() {
      if (!company.value) {
        await fetch();
      }
    }

    const tickets = ref([]);
    const notifyBy = ref('');
    const notifyByEmail = ref(true);

    fetchIfNull();

    const { locale, t } = useI18n();
    const { importCandidates, importCandidatesWithoutInvitation } = useCandidates();
    const templateLink = computed(() => `/static/template_${locale.value.toLowerCase()}.xlsx`);
    const notifyTypes = [
      { value: candidateNotificationTypes.EMAIL, text: t('notifyByEmail'), quotaCode: quotaItemTypes.EMAIL },
      { value: candidateNotificationTypes.EMAIL_AND_SMS, text: t('notifyByEmailAndSMS'), quotaCode: quota.value.sms },
      { value: candidateNotificationTypes.EMAIL_AND_CALL, text: t('notifyByEmailAndCall'), quotaCode: quota.value.calls },
    ];

    const ticketsMoreThanSMSes = computed(() => tickets.value.length > quota.value.sms);
    const ticketsMoreThanCalls = computed(() => tickets.value.length > quota.value.calls);

    return {
      locale: locale.value.toLowerCase(),
      notifyTypes,
      ticketsMoreThanSMSes,
      ticketsMoreThanCalls,
      tickets,
      notifyBy,
      notifyByEmail,
      importCandidates,
      importCandidatesWithoutInvitation,
      templateLink,
      fetchCompany,
      company,
    };
  },
  methods: {
    onButtonClick() {
      this.isSelecting = true;
      window.addEventListener('focus', () => {
        this.isSelecting = false;
      }, { once: true });

      this.$refs.uploader.click();
    },
    onFileChanged(e) {
      this.isWrongFile = false;
      this.selectedFile = e.target.files[0] || this.$t('buttonText');
      this.handleInput(e);
    },
    fixData(data) {
      let o = '';
      let l = 0;
      const w = 10240;
      for (; l < data.byteLength / w; ++l) {
        o += String.fromCharCode.apply(
          null,
          new Uint8Array(data.slice(l * w, l * w + w)),
        );
      }

      o += String.fromCharCode.apply(null, new Uint8Array(data.slice(l * w)));
      return o;
    },
    handleInput(e) {
      e.stopPropagation();
      e.preventDefault();
      const { files } = e.target;
      let i;
      let file;
      for (i = 0, file = files[i]; i !== files.length; ++i) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const data = e.target.result;
          const fixedData = this.fixData(data);
          const workbook = XLSX.read(btoa(fixedData), { type: 'base64' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const tickets = XLSX.utils.sheet_to_json(worksheet);
          this.tickets = this.processTickets(tickets, this.locale);
        };
        reader.readAsArrayBuffer(file);
      }
    },
    processTickets(tickets, locale, isRecurrent) {
      const ticketsProcessed = [];
      let failedValidation = false;
      tickets.forEach((ticket) => {
        const processedTicket = {};

        this.tableData.headers[locale].forEach((header) => {
          if (header.fromData === false) {
            return;
          }

          if (this.tableData.requiredFields.includes(header.value) && !ticket[header.text]) {
            failedValidation = true;
            return;
          }
          processedTicket[header.value] = this.processTicketValue(
            header.value,
            ticket[header.text],
          );
        });

        if (failedValidation) {
          return;
        }

        processedTicket.invitationType = invitationType.ONLINE_INTERVIEW;
        ticketsProcessed.push(processedTicket);
      });

      if (failedValidation && isRecurrent) {
        this.isWrongFile = true;
      }

      if (failedValidation) {
        if (locale === 'ru') {
          return this.processTickets(tickets, 'en', true);
        } if (locale === 'en') {
          return this.processTickets(tickets, 'ru', true);
        }
      }

      if (ticketsProcessed.length === 0) {
        this.isWrongFile = true;
      }

      return ticketsProcessed;
    },
    processTicketValue(valueType, value) {
      switch (valueType) {
        case 'phone': {
          return this.processPhone(value);
        }
        case 'birthDate': {
          return this.processBirthDate(value);
        }
        default: {
          return value;
        }
      }
    },
    processPhone(value) {
      try {
        if (!value) {
          return null;
        }

        const countryCode = this.company?.country?.alpha2Code;
        if (countryCode) {
          let phoneNumber = parsePhoneNumber(`${value}`);

          if (!phoneNumber) {
            phoneNumber = parsePhoneNumber(`${value}`, countryCode);
          }

          if (phoneNumber?.isValid()) {
            return phoneNumber.number;
          }

          this.invalidNumber = true;
          return null;
        }

        return null;
      } catch (e) {
        return null;
      }
    },
    processBirthDate(value) {
      if (typeof value === 'number') {
        const date = new Date(Math.round((value - (25567 + 1)) * 86400 * 1000) - 1);
        return this.$i18n.locale === 'ru'
          ? date.toLocaleDateString('ru-RU')
          : date.toLocaleDateString('en-US');
      }

      if (value && this.validateBirthDate(value)) {
        return value;
      }

      return null;
    },
    cancel() {
      this.tickets = [];
      this.selectedFile = null;
      this.notifyBy = '';
      this.isWrongFile = false;
      this.invalidNumber = false;
    },
    async submit() {
      try {
        const importedCountResponse = this.withInvitation
          ? await this.importWithInvitation()
          : await this.importWithoutInvitation();
        this.importedCount = importedCountResponse?.response?.data?.importedCount;
        this.isImported = true;
      } catch (e) {
        this.cancel();
        this.isWrongFile = true;
      }
    },
    importWithInvitation() {
      return this.importCandidates(
        this.vacancy.id,
        {
          candidates: this.tickets,
          notifyBySms: this.notifyBy === candidateNotificationTypes.SMS,
          notifyByCall: this.notifyBy === candidateNotificationTypes.CALL,
        },
      );
    },
    importWithoutInvitation() {
      return this.importCandidatesWithoutInvitation(
        this.vacancy.id,
        {
          candidates: this.tickets,
        },
      );
    },
    getQuotaAlertText(quotaValue) {
      const quota = this.notifyBy === candidateNotificationTypes.SMS
        ? candidateNotificationTypes.SMS
        : candidateNotificationTypes.CALL;

      return this.$t('quotaAlert', { quotaPlural: this.$tc(quota, 10), quota: this.$tc(quota, 1), quotaValue });
    },
    getQuotaCodeByNotifyBy(notifyBy) {
      switch (notifyBy) {
        case candidateNotificationTypes.SMS: {
          return quotaItemTypes.SMS;
        }
        case candidateNotificationTypes.CALL: {
          return quotaItemTypes.CALLS;
        }
        case candidateNotificationTypes.EMAIL: {
          return quotaItemTypes.EMAIL;
        }
        case candidateNotificationTypes.EMAIL_AND_WHATSAPP: {
          return quotaItemTypes.EMAIL_AND_WHATSAPP;
        }
        default: {
          return '';
        }
      }
    },
    validateBirthDate(value) {
      if (value === null) {
        return true;
      }

      if (value.length !== DATE_FORMATS[this.$i18n.locale].short.length) {
        return false;
      }

      try {
        format(
          parse(value, DATE_FORMATS[this.$i18n.locale].short, new Date()),
          DATE_FORMATS.server.short,
        );
        return true;
      } catch (e) {
        return false;
      }
    },
    checkIfDuplicatedEmail(email) {
      return this.duplicatedEmails.includes(email);
    },
    deleteItem(item) {
      const idx = this.tickets.indexOf(item);
      if (idx > -1) {
        this.tickets.splice(idx, 1);
      }

      if (this.tickets.length === 0) {
        this.selectedFile = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>

.tab {
  position: relative;
  &__button {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background: #F1F6FB;
    border: 1px dashed rgba(31, 133, 215, 0.5);
    box-sizing: border-box;
    border-radius: 4px;
    height: 370px;
    text-decoration: none;
    &:hover {
      border: 1px dashed #1FC57F;
      transform: scale(1.01);
      transition: all 0.25s;
      .tab__button-text {
        text-decoration: underline;
      }
    }
    &-text {
      margin-left: 10px;
    }
    &-download {
      margin-top: 30px;
      text-decoration: none;
      right: 0;
      display: block;
      position: relative;
      width: fit-content;
      margin-left: auto;
      &:hover {
        text-decoration: underline;
      }
    }
  }
}

.notify {
    border: 1px solid #C2C2C2;
    border-radius: 0.25rem;
    padding: 0.812rem 0.9375rem 1px;
    position: relative;
    margin-bottom: 1.25rem;
    &__item {
      margin-bottom: 8px;
    }
    &__rest-hint {
      display: none;
      position: absolute;
      white-space: nowrap;
      cursor: default;
      box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
      border-radius: 5px;
      top: 0;
      transform: translateY(calc(-100% - 15px))  translateX(-50%);
      left: 50%;
      background: #e0e0e0;
      padding: 5px 10px;

      &:after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        width: 15px;
        height: 15px;
        border-radius: 1px;
        transform: rotate(45deg) translate(-80%, 0%);
        background: inherit;
        z-index: -1;
      }
    }
    &__rest-amount {
      position: relative;
      margin-left: 5px;
      &:hover {
        .notify__rest-hint {
          display: block;
        }
      }
    }
  }

  .warning-SMSes, .warning-calls {
    background: #ED2323;
    display: block;
    color: white;

    a {
      color: white;
    }
  }
</style>

<i18n locale="en" lang="yaml">
buttonText: Import candidates
importedCandidatesCount: '{count} candidates for import | {count} candidate for import | {count} candidates for import'
notifyByEmail: Email
notifyByEmailAndSMS: Email + SMS
notifyByEmailAndCall: Email + Robotcall
notifyBySMS: Notify by text message
notifyByCall: Notify by automated call
sms: 'Text Messages | Text Message | Text Messages'
call: 'Calls| Call | Calls'
quotaAlert: There is not enough {quotaPlural} on your account to notify all candidates. We will send {quota} to the first {quotaValue} candidates, the rest of the invitation will be sent only by email.
invite: Invite
cancel: Cancel
invitationsSent: 'The invitation to {count} candidates has been sent. | The invitation to {count} candidate has been sent. | The invitation to {count} candidates has been sent.'
wrongFile.1: An error occurred while processing the file. Please use the
useTemplate: template
wrongFile.2: If you are sure that you did everything correctly, contact
useTechnicalSupport: technical support
downloadTemplate: Download template
duplicatedEmails: Threre are duplicated emails!
invalidEmail: There are invalid emails!
invalidNumber: There are incorrect phone numbers, change them to international format!
notifyByEmailAndSMSTooltip: '{count} SMS left | {count} SMS left | {count} SMS left'
notifyByEmailAndCallTooltip: '{count} calls left | {count} call left | {count} calls left'
</i18n>

<i18n locale="ru" lang="yaml">
buttonText: Импорт кандидатов
importedCandidatesCount: 'Импортировано {count} записей | Импортирована {count} запись | Импортировано {count} записи | Импортировано {count} записей'
notifyByEmail: Email
notifyByEmailAndSMS: Email + SMS
notifyByEmailAndCall: Email + Автоматический звонок
notifyBySMS: Оповестить по SMS
notifyByCall: Оповестить звонком автоинформатора
sms: 'SMS | SMS | SMS | SMS'
call: 'Звоноков | Звонок | Звонка | Звонков'
quotaAlert: На вашем счету недостаточно {quotaPlural}, чтобы оповестить всех кандидатов. Мы отправим {quota} первым {quotaValue} кандидатам, остальным приглашение будет отправлено только по email.
invite: Пригласить
cancel: Отменить
invitationsSent: 'Приглашение {count} кандидатам отправлено. | Приглашение {count} кандидату отправлено. | Приглашение {count} кандидатам отправлено. | Приглашение {count} кандидатам отправлено.'
wrongFile.1: При обработке файла возникла ошибка. Пожалуйста, воспользуйтесь
useTemplate: шаблоном
wrongFile.2: Если вы уверены, что все сделали правильно, обратитесь в
useTechnicalSupport: теххподдержку
downloadTemplate: Скачать шаблон
duplicatedEmails: Есть дублированные email'ы!
invalidEmail: Есть некорректные email'ы!
invalidNumber: Есть некорректные номера телефонов, измените их на международный формат!
emailAndWhatsapp: Email + WhatsApp
notifyByEmailAndSMSTooltip: 'Осталось {count} SMS | Осталось {count} SMS | Осталось {count} SMS | Осталось {count} SMS'
notifyByEmailAndCallTooltip: 'Осталось {count} звонков | Остался {count} звонок | Осталось {count} звонка | Осталось {count} звонков'
</i18n>
